import { useEffect, useState } from 'react';
import type {
  ParticipationForm,
  ParticipationFormQuestion,
  FormAnswers,
  FormAnswer,
} from '../../shared/forms';
import type { ClientSession } from '../api';
import { participationRequest, participationSignIn } from '../participation-api';
import '../components/participation.css';

export function QuestionFields({
  questions,
  answers,
  change,
  disabled = false,
}: {
  questions: ParticipationFormQuestion[];
  answers: FormAnswers;
  change: (id: string, answer: FormAnswer) => void;
  disabled?: boolean;
}) {
  return (
    <>
      {questions.map((question) => (
        <fieldset className="participation-question" key={question.id} disabled={disabled}>
          <legend>
            {question.label}
            {question.required ? ' (required)' : ' (optional)'}
          </legend>
          {question.description && <p>{question.description}</p>}
          {(question.type === 'single' || question.type === 'multiple') &&
            question.options.map((option) => (
              <label key={option.id}>
                <input
                  type={question.type === 'single' ? 'radio' : 'checkbox'}
                  name={question.id}
                  required={question.type === 'single' && question.required}
                  checked={
                    question.type === 'single'
                      ? answers[question.id] === option.id
                      : Array.isArray(answers[question.id]) &&
                        (answers[question.id] as string[]).includes(option.id)
                  }
                  onChange={(e) => {
                    const selected = Array.isArray(answers[question.id])
                      ? (answers[question.id] as string[])
                      : [];
                    change(
                      question.id,
                      question.type === 'single'
                        ? option.id
                        : e.target.checked
                          ? [...selected, option.id]
                          : selected.filter((id) => id !== option.id),
                    );
                  }}
                />
                {option.label}
              </label>
            ))}
          {question.type === 'availability' &&
            question.options.map((option) => (
              <label className="field" key={option.id}>
                {option.label}
                <select
                  required={question.required}
                  value={
                    (answers[question.id] as Record<string, string> | undefined)?.[option.id] || ''
                  }
                  onChange={(e) =>
                    change(question.id, {
                      ...(typeof answers[question.id] === 'object' &&
                      !Array.isArray(answers[question.id])
                        ? (answers[question.id] as Record<string, 'yes' | 'if-needed' | 'no'>)
                        : {}),
                      [option.id]: e.target.value as 'yes' | 'if-needed' | 'no',
                    })
                  }
                >
                  <option value="">Choose availability</option>
                  <option value="yes">Yes</option>
                  <option value="if-needed">If needed</option>
                  <option value="no">No</option>
                </select>
              </label>
            ))}
          {question.type === 'rating' && (
            <label className="field">
              Your rating
              <select
                required={question.required}
                value={(answers[question.id] as number | undefined) ?? ''}
                onChange={(e) => change(question.id, Number(e.target.value))}
              >
                <option value="">Choose a rating</option>
                {Array.from(
                  { length: question.max - question.min + 1 },
                  (_, index) => question.min + index,
                ).map((number) => (
                  <option key={number} value={number}>
                    {number}
                    {number === question.min && question.minLabel
                      ? ` - ${question.minLabel}`
                      : number === question.max && question.maxLabel
                        ? ` - ${question.maxLabel}`
                        : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          {question.type === 'long-text' && (
            <label className="field">
              Your answer
              <textarea
                rows={4}
                required={question.required}
                maxLength={5000}
                value={(answers[question.id] as string) || ''}
                onChange={(e) => change(question.id, e.target.value)}
              />
            </label>
          )}
          {(question.type === 'short-text' || question.type === 'date') && (
            <label className="field">
              Your answer
              <input
                type={question.type === 'date' ? 'date' : 'text'}
                required={question.required}
                maxLength={500}
                value={(answers[question.id] as string) || ''}
                onChange={(e) => change(question.id, e.target.value)}
              />
            </label>
          )}
        </fieldset>
      ))}
    </>
  );
}
export function ParticipationPage({
  session,
  formId,
  path,
}: {
  session: ClientSession | null;
  formId?: string;
  path?: string;
}) {
  const requestedMaintenanceId = new URLSearchParams(location.search).get('response');
  const [maintenanceId, setMaintenanceId] = useState(requestedMaintenanceId || '');
  const [forms, setForms] = useState<ParticipationForm[]>();
  const [form, setForm] = useState<ParticipationForm>();
  const [answers, setAnswers] = useState<FormAnswers>({});
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState('');
  const [editing, setEditing] = useState(true);
  const load = async () => {
    if (requestedMaintenanceId) return;
    if (!formId && !path) {
      const result = await participationRequest<{ forms: ParticipationForm[] }>(
        '/api/participation/forms',
        session,
      );
      setForms(result.forms);
    } else {
      const result = await participationRequest<{ form: ParticipationForm }>(
        formId
          ? `/api/participation/forms/${encodeURIComponent(formId)}`
          : `/api/participation/form-page?path=${encodeURIComponent(path!)}`,
        session,
      );
      setForm(result.form);
      if (result.form.ownResponse)
        sessionStorage.setItem(`participation-response-form:${result.form.path}`, result.form.id);
      setAnswers(result.form.ownResponse?.answers || {});
      setEditing(!result.form.ownResponse || result.form.ownResponse.withdrawn);
    }
  };
  useEffect(() => {
    void load().catch((cause) => {
      const storedId =
        formId || (path ? sessionStorage.getItem(`participation-response-form:${path}`) : null);
      if (storedId) setMaintenanceId(storedId);
      else setError(cause.message);
    });
  }, [formId, path, session?.user?.id]);
  const save = async (withdraw = false) => {
    if (!form) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await participationRequest<{ receipt?: string }>(
        `/api/participation/forms/${form.id}/respond`,
        session,
        withdraw ? 'DELETE' : 'PUT',
        withdraw
          ? { ...(receipt ? { receipt } : {}) }
          : { answers, ...(receipt ? { receipt } : {}) },
      );
      sessionStorage.setItem(`participation-response-form:${form.path}`, form.id);
      if (result?.receipt) setReceipt(result.receipt);
      if (form.identityMode === 'identified') await load();
      else setEditing(withdraw);
      setNotice(
        withdraw ? 'Your response was withdrawn.' : 'Thank you. Your response has been saved.',
      );
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (maintenanceId) return <ResponseMaintenance id={maintenanceId} session={session} />;
  return (
    <main id="main-content" className="participation-page">
      <a href="/">← Calendar</a>
      {!formId && !path ? (
        <>
          <h1>Polls and feedback</h1>
          <p>Share your ideas and help plan our school community.</p>
          {session?.capabilities.staff && (
            <a className="button" href="/participation/new">
              Create a poll or form
            </a>
          )}
          {forms?.map((item) => (
            <article className="participation-card" key={item.id}>
              <h2>
                <a href={item.path}>{item.title}</a>
              </h2>
              <p>{item.intro}</p>
              <p>
                {item.effectiveStatus || item.status}
                {item.pilot ? ' · Staff pilot' : ''}
              </p>
              {item.canManage && (
                <a href={`/participation/${item.id}/manage`}>Manage poll or form</a>
              )}
            </article>
          ))}
          {forms?.length === 0 && <p>There are no polls or forms available right now.</p>}
        </>
      ) : form ? (
        <article className="participation-card">
          <h1>{form.title}</h1>
          <p>{form.intro}</p>
          {form.pilot && (
            <p className="participation-private-note">Staff pilot. These are test responses.</p>
          )}
          <p className="participation-private-note">
            {form.identityMode === 'identified'
              ? 'Your account name and answers are visible only to authorized result readers.'
              : 'This form collects answers without your account name. Free text can identify you, so avoid including personal details. Save your private receipt to change or withdraw your response.'}
          </p>
          {form.closesAt && <p>Closes {new Date(form.closesAt).toLocaleString()}.</p>}
          {!form.collectionAvailable && (
            <p role="status">
              {form.blockedReason || 'This form is not accepting responses right now.'}
            </p>
          )}
          {form.identityMode === 'identified' && !session?.user ? (
            <>
              <p>Please sign in with an eligible account to respond.</p>
              <button
                className="button"
                onClick={() => {
                  void participationSignIn('form', form.id, session).catch((cause) =>
                    setError(cause.message),
                  );
                }}
              >
                Sign in to respond
              </button>
            </>
          ) : editing ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              <QuestionFields
                questions={form.questions}
                answers={answers}
                change={(id, answer) => setAnswers({ ...answers, [id]: answer })}
                disabled={!form.collectionAvailable || busy}
              />
              <button className="button" disabled={busy || !form.collectionAvailable}>
                {busy ? 'Saving…' : 'Submit response'}
              </button>
            </form>
          ) : (
            <div className="participation-actions">
              <button
                className="button"
                disabled={!form.collectionAvailable}
                onClick={() => setEditing(true)}
              >
                Change response
              </button>
              <button
                className="button button-quiet"
                disabled={busy}
                onClick={() => void save(true)}
              >
                Withdraw response
              </button>
            </div>
          )}
          {form.identityMode === 'anonymous' && (
            <details className="participation-receipt" open={Boolean(receipt)}>
              <summary>Private response receipt</summary>
              <p>
                Keep this private. Anyone with this receipt may manage this response. It is not an
                account login.
              </p>
              <label className="field">
                Receipt
                <input
                  value={receipt}
                  onChange={(e) => setReceipt(e.target.value)}
                  autoComplete="off"
                />
              </label>
              {receipt && (
                <button
                  className="button button-quiet"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    void participationRequest<{ form: ParticipationForm }>(
                      `/api/participation/forms/${form.id}`,
                      session,
                      'GET',
                      undefined,
                      { 'X-Participation-Receipt': receipt },
                    )
                      .then((result) => {
                        setForm(result.form);
                        setAnswers(result.form.ownResponse?.answers || {});
                        setEditing(true);
                        setNotice('Your saved response is ready to review.');
                      })
                      .catch((cause) => setError(cause.message))
                      .finally(() => setBusy(false));
                  }}
                >
                  Load my response
                </button>
              )}
              {receipt && (
                <button
                  className="button button-quiet"
                  disabled={busy}
                  onClick={() => void save(true)}
                >
                  Withdraw using receipt
                </button>
              )}
            </details>
          )}
          <p>
            <a href={`/participation?response=${encodeURIComponent(form.id)}`}>
              Manage or withdraw my response
            </a>
          </p>
          {form.canManage && (
            <p>
              <a href={`/participation/${form.id}/manage`}>
                Manage this {form.kind === 'poll' ? 'poll' : 'form'}
              </a>
            </p>
          )}
        </article>
      ) : (
        !error && <p role="status">Loading…</p>
      )}
      {error && (
        <p role="alert" className="inline-error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="inline-success">
          {notice}
        </p>
      )}
    </main>
  );
}

function ResponseMaintenance({ id, session }: { id: string; session: ClientSession | null }) {
  const [receipt, setReceipt] = useState('');
  const [found, setFound] = useState(false);
  const [withdrawn, setWithdrawn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await participationRequest<{ response: { withdrawn: boolean } }>(
        `/api/participation/forms/${encodeURIComponent(id)}/respond`,
        session,
        'GET',
        undefined,
        receipt ? { 'X-Participation-Receipt': receipt } : {},
      );
      setFound(true);
      setWithdrawn(result.response.withdrawn);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (session?.user) void load();
  }, [id, session?.user?.id]);
  const withdraw = async () => {
    setBusy(true);
    setError('');
    try {
      await participationRequest(
        `/api/participation/forms/${encodeURIComponent(id)}/respond`,
        session,
        'DELETE',
        receipt ? { receipt } : {},
      );
      setWithdrawn(true);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main id="main-content" className="participation-page">
      <a href="/">← Calendar</a>
      <section className="participation-card">
        <h1>Manage your response</h1>
        <p>
          This page only manages your own response. It does not show the original form or other
          people's answers.
        </p>
        {!found && (
          <>
            {!session?.user && (
              <p>
                <a
                  href={`/login?callbackURL=${encodeURIComponent(location.pathname + location.search)}`}
                >
                  Sign in to manage a named response
                </a>
                , or use your private anonymous receipt.
              </p>
            )}
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void load();
              }}
            >
              <label className="field">
                Private response receipt
                <input
                  value={receipt}
                  onChange={(event) => setReceipt(event.target.value)}
                  autoComplete="off"
                />
              </label>
              <button className="button" disabled={busy}>
                Find my response
              </button>
            </form>
          </>
        )}
        {found &&
          (withdrawn ? (
            <p role="status">Your response was withdrawn.</p>
          ) : (
            <>
              <p>Your response is still active.</p>
              <button className="button" disabled={busy} onClick={() => void withdraw()}>
                Withdraw my response
              </button>
            </>
          ))}
        {error && <p role="alert">{error}</p>}
      </section>
    </main>
  );
}
